/**
 * @desc : 企业中心 => 设置 => 企业设置页
 */

import { Button, Form, Input, message, Spin, Modal } from 'antd';
import {
    setEntInformation,
    getAndSetCorporateBankAccount,
} from '@/api/personCenter/organization';
import { useStoreState } from '&/utils/customHooks';
import { CheckCircleOutlined } from '@ant-design/icons';
import BaseModal from '&/components/BaseModal';
import BasePageWrap from '&/components/ResourceLib/BasePageWrap';

import './EnterpriseInfo.scss';

const layout = {
    labelCol: {
        span: 2,
    },
    wrapperCol: {
        span: 4,
    },
};

const EnterpriseInfo = () => {
    const { organization = {} } = useStoreState('appStore');

    const entId = organization.branchId || organization.enterpriseId;

    //1-全屋定制、2-装修公司、3-成品家具、4-铝门窗、5-房产/物业、99-其他
    const industryObj = {
        1: intl.get('userCenterSidebar.customWholeHouseDesign'),
        2: intl.get('userCenterSidebar.decorationCompany'),
        3: intl.get('userCenterSidebar.finishedFurniture'),
        4: intl.get('userCenterSidebar.aluminumDoorsAndWindows'),
        5: intl.get('userCenterSidebar.realEstateProperty'),
        99: intl.get('userCenterSidebar.other'),
    };

    // 企业规模
    const enterpriseScale = {
        0: intl.get('userCenterSidebar.lessThan10'),
        1: intl.get('userCenterSidebar.from10To20'),
        2: intl.get('userCenterSidebar.from20To50'),
        3: intl.get('userCenterSidebar.from50To100'),
        4: intl.get('userCenterSidebar.from100To500'),
        5: intl.get('userCenterSidebar.from500To1000'),
        6: intl.get('userCenterSidebar.moreThan1000'),
    };

    const onFinish = values => {
        setLoading(true);
        setEntInformation({
            mobile: values.mobile,
            email: values.email,
            contacts: values.addressBook,
            entId,
            dap: 2,
        })
            .then(() => {
                message.success(intl.get('userCenterSidebar.saveSuccess'));
            })
            .finally(() => {
                setLoading(false);
            });
    };

    const { Item, useForm } = Form;
    const [formRef] = useForm();
    const [loading, setLoading] = useState(false);
    const [entDetails, setEntDetails] = useState({});

    function initForm() {
        return setEntInformation({ entId }).then(({ data }) => {
            setLoading(false);
            const detail = data?.detail || {};
            let {
                status: certification,
                name: entName,
                industry,
                scale: size,
                address,
                contacts: addressBook,
                mobile,
                email,
            } = detail;

            if (+certification === 3) {
                certification = '已认证';
            }
            const detailsData = {
                certification,
                entName,
                industry: industryObj[industry],
                size: enterpriseScale[size],
                address,
                addressBook: addressBook || '',
                mobile: mobile || '',
                email: email || '',
            };
            formRef.setFieldsValue(detailsData);
            setEntDetails(detailsData);
        });
    }

    const [bankAccount, setBankAccount] = useState(null);

    const queryBankInfo = () => {
        return getAndSetCorporateBankAccount({ dap: 1 }).then(({ data }) => {
            setBankAccount(data.detail);
        });
    };

    useEffect(() => {
        setLoading(true);
        Promise.allSettled([initForm(), queryBankInfo()]).finally(() => {
            setLoading(false);
        });
    }, []);

    const [visible, setVisible] = useState(false);

    const closeModal = () => {
        bankForm.resetFields();
        setVisible(false);
    };

    const [bankForm] = useForm();
    const showBankForm = () => {
        bankForm.setFieldsValue(bankAccount || {});
        setVisible(true);
    };

    const [modalLoading, setModalLoading] = useState(false);
    const onVerification = values => {
        setModalLoading(true);
        getAndSetCorporateBankAccount({ dap: 2, ...values })
            .then(({ msg }) => {
                message.success(msg);
                setBankAccount(values);
                closeModal();
            })
            .finally(() => {
                setModalLoading(false);
            });
    };

    const onDelete = () => {
        const modal = Modal.confirm({
            content: '确定当前收款信息？',
            okText: intl.get('base.delete'),
            cancelText: intl.get('base.cancel'),
            okButtonProps: { loading: false },
            width: 256,
            centered: true,
            onOk: async () => {
                modal.update({ okButtonProps: { loading: true } });
                try {
                    const { msg } = await getAndSetCorporateBankAccount({
                        dap: 3,
                    });
                    message.success(msg);
                    queryBankInfo();
                } finally {
                    modal.update({ okButtonProps: { loading: false } });
                }
            },
        });
    };

    return (
        <BasePageWrap
            title={
                <strong>
                    {intl.get('userCenterSidebar.enterpriseInformation')}
                </strong>
            }
            className="ent-info"
            type="card">
            <Spin spinning={loading}>
                <Form
                    {...layout}
                    form={formRef}
                    className="ent-form"
                    onFinish={onFinish}>
                    <Item
                        name="certification"
                        label={intl.get(
                            'userCenterSidebar.enterpriseAuthentication'
                        )}>
                        <Input
                            prefix={
                                <CheckCircleOutlined
                                    style={{ color: 'green' }}
                                />
                            }
                            bordered={false}
                            readOnly
                        />
                    </Item>
                    <Item
                        // name="entName"
                        label={intl.get('userCenterSidebar.enterpriseName')}>
                        {/*<Input bordered={false} readOnly />*/}
                        <p className="text-no-wrap" title={entDetails.entName}>
                            {entDetails.entName}
                        </p>
                    </Item>
                    <Item
                        name="industry"
                        label={intl.get('userCenterSidebar.industryType')}>
                        <Input bordered={false} readOnly />
                    </Item>
                    <Item
                        name="size"
                        label={intl.get('userCenterSidebar.enterpriseScale')}>
                        <Input bordered={false} readOnly />
                    </Item>
                    <Item
                        // name="address"
                        label={intl.get('userCenterSidebar.enterpriseAddress')}>
                        {/*<Input bordered={false} readOnly />*/}
                        <p className="text-no-wrap" title={entDetails.address}>
                            {entDetails.address}
                        </p>
                    </Item>

                    <Item
                        name="addressBook"
                        label={intl.get('userCenterSidebar.contactPerson')}
                        rules={[{ required: true }]}>
                        <Input maxLength={50} />
                    </Item>
                    <Item
                        name="mobile"
                        label={intl.get('userCenterSidebar.mobile')}
                        rules={[
                            { required: true },
                            {
                                pattern: /^1[1|2|3|4|5|7|8|9][0-9]\d{8}$/,
                                message: intl.get(
                                    'signUpSignIn.inputCorrectPhoneNumber'
                                ),
                            },
                        ]}>
                        <Input />
                    </Item>
                    <Item
                        name="email"
                        label={intl.get('userCenterSidebar.email')}
                        rules={[
                            { required: true },
                            {
                                type: 'email',
                            },
                        ]}>
                        <Input />
                    </Item>
                    <Item label="收款信息">
                        {bankAccount ? (
                            <div className="bank-account">
                                <div className="bank-account-text">
                                    {bankAccount.account}
                                    <div
                                        className="text-no-wrap"
                                        title={
                                            bankAccount.num +
                                            ' ' +
                                            bankAccount.bankName
                                        }>
                                        {bankAccount.num} {bankAccount.bankName}
                                    </div>
                                </div>
                                <div
                                    className="icon-wrap"
                                    onClick={showBankForm}>
                                    <i className="outlined icon-Icon-Edit"></i>
                                </div>
                                <div className="icon-wrap" onClick={onDelete}>
                                    <i className="outlined icon-trash"></i>
                                </div>
                            </div>
                        ) : (
                            <div className="native-link" onClick={showBankForm}>
                                +收款信息
                            </div>
                        )}
                    </Item>
                    <Item
                        wrapperCol={{
                            ...layout.wrapperCol,
                            offset: 2,
                        }}>
                        <Button
                            type="primary"
                            /* disabled={
                                organization.example === 0 &&
                                organization.roleFlag !== 'admin'
                            } */
                            htmlType="submit">
                            {intl.get('base.save')}
                        </Button>
                    </Item>
                </Form>
            </Spin>
            <BaseModal
                className="edit-ent-info"
                visible={visible}
                width={389}
                title="收款信息"
                footer={null}
                forceRender={true}
                destroyOnClose
                onCancel={closeModal}>
                <Form
                    autoComplete="off"
                    form={bankForm}
                    preserve={false}
                    labelCol={{ span: 6 }}
                    onFinish={onVerification}>
                    <Item
                        name="account"
                        rules={[
                            {
                                required: true,
                                whitespace: true,
                            },
                        ]}
                        messageVariables={{
                            label: '户号',
                        }}
                        label={
                            <>户&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;号</>
                        }>
                        <Input maxLength={30} placeholder="请输入户号" />
                    </Item>
                    <Item
                        name="num"
                        validateFirst
                        rules={[
                            {
                                required: true,
                            },
                            {
                                pattern: /^(?:\d{6,20})$/,
                                message: '银行账号格式错误',
                            },
                        ]}
                        label="银行账号">
                        <Input placeholder="请输入银行账号" />
                    </Item>
                    <Item
                        name="bankName"
                        rules={[
                            {
                                required: true,
                            },
                        ]}
                        label="开户银行">
                        <Input placeholder="请输入开户银行" />
                    </Item>
                </Form>
                <div className="btns-wrap">
                    <Button
                        onClick={closeModal}
                        children={intl.get('base.cancel')}
                    />
                    <Button
                        type="primary"
                        loading={modalLoading}
                        onClick={bankForm.submit}
                        children={intl.get('base.confirm')}
                    />
                </div>
            </BaseModal>
        </BasePageWrap>
    );
};

export default EnterpriseInfo;
